import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

const isNative = () => Boolean(window.Capacitor?.isNativePlatform());

const parseAppTokenFromUrl = (url) => {
  // Server-side redirect delivers app JWT as ?token= query param
  const query = url.split('?')[1] || '';
  return new URLSearchParams(query).get('token') || '';
};

const AuthContext = createContext(null);

const STORAGE_KEY = 'pc_auth_token';
const USER_KEY = 'pc_auth_user';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || '');
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const persistAuth = useCallback((nextToken, nextUser) => {
    setToken(nextToken);
    setUser(nextUser);
    localStorage.setItem(STORAGE_KEY, nextToken);
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
  }, []);

  const clearAuth = useCallback(() => {
    setToken('');
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const register = useCallback(async (payload) => {
    const res = await axios.post(`${API_BASE_URL}/auth/register`, payload);
    persistAuth(res.data.access_token, res.data.user);
    return res.data.user;
  }, [persistAuth]);

  const login = useCallback(async (payload) => {
    const res = await axios.post(`${API_BASE_URL}/auth/login`, payload);
    persistAuth(res.data.access_token, res.data.user);
    return res.data.user;
  }, [persistAuth]);

  const loginWithGoogleToken = useCallback(async (idToken) => {
    const res = await axios.post(`${API_BASE_URL}/auth/google`, { id_token: idToken });
    persistAuth(res.data.access_token, res.data.user);
    return res.data.user;
  }, [persistAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`);
    } catch (_) {
      // no-op
    }
    clearAuth();
  }, [clearAuth]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    const verify = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/auth/me`, { headers: authHeaders });
        if (!cancelled) {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        }
      } catch (_) {
        if (!cancelled) clearAuth();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [token, authHeaders, clearAuth]);

  // Handle poupix://auth/callback deep link from native Google OAuth flow.
  useEffect(() => {
    if (!isNative()) return;

    let listener;
    const setup = async () => {
      const { App } = await import('@capacitor/app');
      listener = await App.addListener('appUrlOpen', async (event) => {
        if (!event.url.startsWith('poupix://auth')) return;
        const appToken = parseAppTokenFromUrl(event.url);
        if (!appToken) return;
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.close();
          // Fetch user profile using the app JWT issued by the server
          const res = await axios.get(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${appToken}` },
          });
          persistAuth(appToken, res.data);
          localStorage.removeItem('pc_google_state');
        } catch (_) {
          // Auth error handled in the calling UI.
        }
      });
    };

    setup();
    return () => {
      listener?.remove?.();
    };
  }, [persistAuth]);

  const value = {
    user,
    token,
    isAuthenticated: Boolean(token && user),
    loading,
    authHeaders,
    register,
    login,
    loginWithGoogleToken,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
